// Resource module — the notifications hub's in-app inbox (/notifications/*).
// React-free. The wire shapes come from the generated OpenAPI contract
// (components['schemas']['Notification']), which the backend sources from the
// vendored hub's own declaration — so this file invents no field.
//
// GET /notifications/inbox    → {inbox: Notification[], nextCursor: string|null}
// POST /notifications/inbox/{notificationId}/read → {read: true}
// POST /notifications/inbox/read-all → {marked: number}
// POST /notifications/inbox/{notificationId}/archive → {archived: true}
//
// All routes are org-scoped AND per-user: the hub filters user_id + org_id
// from the authenticated session, so the console cannot (and must not) name a
// recipient. `?orgId=` rides every call through apiFetch's `orgId` option.
// read-all/archive are the product `notifications-inbox` module's routes —
// the packaged hub ships the services but does not mount them.
import { apiFetch } from '@/lib/api'
import { toConsolePath } from '@/lib/consolePath'
import type { components } from '@/types/openapi'

/** One inbox row, exactly as GET /notifications/inbox serves it. */
export type Notification = components['schemas']['Notification']

/** The three-way split the hub's `status` filter supports (plus 'all'). */
export type InboxStatus = 'unread' | 'read' | 'archived' | 'all'

/** Peeled shape of GET /notifications/inbox — the page object as-is. */
export interface InboxPage {
  inbox: Notification[]
  nextCursor: string | null
  /** Org-wide unread badge count (read_at IS NULL) — unaffected by ?status= or ?cursor=. */
  unreadTotal: number
}

// Key contract: [domain, orgId, leaf, ...params] — see OrgQueryKey in @/lib/queries.
export const notificationsKeys = {
  all: (orgId: string | null) => ['notifications', orgId] as const,
  inbox: (status: InboxStatus) => (orgId: string | null) => ['notifications', orgId, 'inbox', status] as const,
}

export function fetchInbox(
  orgId: string,
  params?: { status?: InboxStatus; limit?: number; cursor?: string },
): Promise<InboxPage> {
  return apiFetch<InboxPage>('/notifications/inbox', { orgId, params })
}

/** Mark one notification read. Idempotent server-side; another user's id is a 404. */
export function markNotificationRead(orgId: string, notificationId: string): Promise<{ read: true }> {
  return apiFetch<{ read: true }>(`/notifications/inbox/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST',
    orgId,
  })
}

/** Mark EVERY unread notification read in one call. Returns the count marked. */
export function markAllNotificationsRead(orgId: string): Promise<{ marked: number }> {
  return apiFetch<{ marked: number }>('/notifications/inbox/read-all', { method: 'POST', orgId })
}

/**
 * Archive one notification (dismissal — the row stays listable under
 * status=archived; there is no unarchive route). Idempotent; another
 * user's id is a 404.
 */
export function archiveNotification(orgId: string, notificationId: string): Promise<{ archived: true }> {
  return apiFetch<{ archived: true }>(
    `/notifications/inbox/${encodeURIComponent(notificationId)}/archive`,
    { method: 'POST', orgId },
  )
}

/**
 * The item's action target — the console path a notice wants the reader to
 * open (`data.actionUrl`), or null when it carries none.
 *
 * Only a CONSOLE-RELATIVE path is accepted. `data` is free-form JSON written by
 * whichever backend module produced the notice, and an inbox row must never be
 * able to turn into a redirect to another origin: an absolute URL (`https://…`),
 * a protocol-relative one (`//host/…`, an absolute URL in disguise) and a
 * backslash variant (`/\host/…` — browsers collapse `\` to `/`, so it IS
 * `//host/…` the moment the rendered href leaves the router) are all rejected,
 * as is any whitespace/control char. A notice without an action is normal —
 * it renders as copy only, which is what most notices are.
 */
export function notificationActionPath(item: Notification): string | null {
  return toConsolePath(item.data?.['actionUrl'])
}
